import React from "react";
import { Link } from "react-router-dom";
import { fmtMoney } from "@/lib/format";
import { FileText, CreditCard, Clock, CheckCircle, ArrowRight, AlertTriangle, Users as UsersIcon } from "lucide-react";
import StatCard from "@/components/dashboard/StatCard";

const StatSkeleton = () => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="h-[104px] rounded-xl border border-border bg-card animate-pulse" />
    ))}
  </div>
);

const DashboardStats = ({ stats, isAdmin, loading }) => {
  if (loading && !stats) return <StatSkeleton />;
  if (!stats) return null;

  if (isAdmin) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total users" value={stats.total_users ?? 0} icon={UsersIcon} hint="All registered users" />
        <StatCard label="Total contracts" value={stats.total_contracts ?? 0} icon={FileText} hint="All contracts on the platform" />
        <StatCard label="Active contracts" value={stats.active_contracts ?? 0} icon={CheckCircle} hint="Contracts currently in progress" />
        <StatCard label="Open disputes" value={stats.open_disputes ?? 0} icon={AlertTriangle} hint="Disputes awaiting resolution" />
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/contracts?status=active" data-testid="tile-active-contracts">
          <StatCard label="Active contracts" value={stats.active_contracts ?? 0} icon={FileText} hint="Contracts currently in progress" />
        </Link>
        <Link to="/contracts?filter=paying&status=reserved-payout" data-testid="tile-in-escrow">
          <StatCard label="Reserved Pay Out" value={fmtMoney(stats.total_in_escrow ?? 0)} icon={CreditCard} hint="Across contracts you're paying" />
        </Link>
        <Link to="/contracts?status=pending-approval" data-testid="tile-pending-approval">
          <StatCard label="Pending approval" value={stats.pending_approvals ?? 0} icon={Clock} hint="Submitted work to review" />
        </Link>
        <Link to="/transactions" data-testid="tile-paid-month">
          <StatCard label="Paid this month" value={fmtMoney(stats.paid_this_month ?? 0)} icon={CheckCircle} hint="Released to you" />
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Link to="/contracts?status=needs-funding" data-testid="tile-needs-funding">
          <StatCard label="Needs funding" value={stats.needs_funding ?? 0} icon={CreditCard} hint="Milestones waiting on escrow funding" />
        </Link>
        <Link to="/contracts?status=ready-to-release" data-testid="tile-ready-release">
          <StatCard label="Ready to release" value={stats.ready_to_release ?? 0} icon={ArrowRight} hint="Approved work awaiting payment release" />
        </Link>
        <Link to="/disputes" data-testid="tile-open-disputes">
          <StatCard label="Open disputes" value={stats.open_disputes ?? 0} icon={AlertTriangle} hint="Disputes awaiting resolution" />
        </Link>
      </div>
    </>
  );
};

export default DashboardStats;
